import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useQueryClient} from 'react-query'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import styles from './kiosk.module.css'
import {KioskBoard} from './KioskBoard'
import {
  clearKioskToken,
  getKioskPairStatus,
  getKioskToken,
  postKioskPair,
  setKioskToken,
} from './kioskApi'

const PAIR_POLL_MS = 3000
const RETRY_MS = 10000
// Recarrega a página periodicamente para liberar memória em navegadores de TV
const RELOAD_EVERY_MS = 6 * 60 * 60 * 1000

type Phase = 'loading' | 'pairing' | 'active' | 'error'

const requestFullscreen = () => {
  const element: any = document.documentElement
  if (document.fullscreenElement) return
  const request = element.requestFullscreen || element.webkitRequestFullscreen
  if (request) {
    try {
      const result = request.call(element)
      if (result && typeof result.catch === 'function') result.catch(() => undefined)
    } catch (error) {
      // navegador não permite
    }
  }
}

// Evita que a TV apague a tela (quando o navegador suporta)
const useWakeLock = () => {
  useEffect(() => {
    const nav: any = navigator
    if (!nav.wakeLock) return

    let lock: any = null
    const acquire = async () => {
      try {
        lock = await nav.wakeLock.request('screen')
      } catch (error) {
        lock = null
      }
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') acquire()
    }

    acquire()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (lock) lock.release().catch(() => undefined)
    }
  }, [])
}

const useKioskEnvironment = () => {
  useEffect(() => {
    const html = document.documentElement
    const previousTheme = html.getAttribute('data-bs-theme')
    html.setAttribute('data-bs-theme', 'dark')
    document.title = 'Vision — Modo TV'

    const reloadTimer = setTimeout(() => window.location.reload(), RELOAD_EVERY_MS)

    window.addEventListener('click', requestFullscreen)
    window.addEventListener('keydown', requestFullscreen)

    return () => {
      if (previousTheme) html.setAttribute('data-bs-theme', previousTheme)
      clearTimeout(reloadTimer)
      window.removeEventListener('click', requestFullscreen)
      window.removeEventListener('keydown', requestFullscreen)
    }
  }, [])

  useWakeLock()
}

const KioskPage: FC = () => {
  useKioskEnvironment()

  const queryClient = useQueryClient()
  const [phase, setPhase] = useState<Phase>('loading')
  const [code, setCode] = useState<string>('')
  const [expiresAt, setExpiresAt] = useState<number>(0)
  const [secondsLeft, setSecondsLeft] = useState<number>(0)
  const pairingRef = useRef(false)

  const startPairing = useCallback(async () => {
    if (pairingRef.current) return
    pairingRef.current = true

    try {
      clearKioskToken()
      const result = await postKioskPair()
      setKioskToken(result.deviceToken)
      setCode(result.code)
      setExpiresAt(new Date(result.expiresAt).getTime())
      setPhase('pairing')
    } catch (error) {
      setPhase('error')
    } finally {
      pairingRef.current = false
    }
  }, [])

  // Decide o estado inicial: TV já autorizada entra direto no painel
  const bootstrap = useCallback(async () => {
    if (!getKioskToken()) {
      startPairing()
      return
    }

    try {
      const {status} = await getKioskPairStatus()
      if (status === 'ACTIVE') setPhase('active')
      else startPairing()
    } catch (error) {
      setPhase('error')
    }
  }, [startPairing])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  // Sem conexão com o servidor: tenta de novo sozinho
  useEffect(() => {
    if (phase !== 'error') return
    const timer = setTimeout(bootstrap, RETRY_MS)
    return () => clearTimeout(timer)
  }, [phase, bootstrap])

  // Aguardando a empresa digitar o código
  useEffect(() => {
    if (phase !== 'pairing') return

    const timer = setInterval(async () => {
      try {
        const {status} = await getKioskPairStatus()
        if (status === 'ACTIVE') setPhase('active')
        else if (status !== 'PENDING') startPairing()
      } catch (error) {
        // tenta de novo no próximo ciclo
      }
    }, PAIR_POLL_MS)

    return () => clearInterval(timer)
  }, [phase, startPairing])

  // Contagem regressiva do código; ao expirar gera outro
  useEffect(() => {
    if (phase !== 'pairing' || !expiresAt) return

    const tick = () => {
      const left = Math.max(0, Math.round((expiresAt - Date.now()) / 1000))
      setSecondsLeft(left)
      if (left === 0) startPairing()
    }

    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [phase, expiresAt, startPairing])

  // TV revogada pela empresa: descarta os dados em cache e volta para o código
  const handleUnauthorized = useCallback(() => {
    queryClient.removeQueries('kiosk-plants')
    queryClient.removeQueries('kiosk-summary')
    queryClient.removeQueries('kiosk-logo')
    startPairing()
  }, [queryClient, startPairing])

  return (
    <div className={styles.root}>
      {phase === 'active' && <KioskBoard onUnauthorized={handleUnauthorized} />}

      {phase === 'loading' && <div className={styles.center}>Conectando...</div>}

      {phase === 'error' && (
        <div className={styles.center}>Sem conexão com o servidor. Tentando novamente...</div>
      )}

      {phase === 'pairing' && (
        <div className={styles.pairing}>
          <img
            className={styles.pairingLogo}
            src={toAbsoluteUrl('/media/imgs/novalogo.png')}
            alt='Solar Vision'
          />
          <div className={styles.pairingTitle}>Conectar esta TV</div>
          <div className={styles.pairingHint}>
            Acesse o <strong>Solar Vision</strong> com a conta da empresa ou filial, abra{' '}
            <strong>Dispositivos TV</strong> e digite o código abaixo.
          </div>
          <div className={styles.code}>
            {code.split('').map((char, index) => (
              <span key={index} className={styles.codeChar}>{char}</span>
            ))}
          </div>
          <div className={styles.pairingFooter}>
            O código expira em {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
          </div>
        </div>
      )}
    </div>
  )
}

export {KioskPage}
